/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Guitar, ChevronDown, ChevronUp } from 'lucide-react';
import ChordFingeringSelector from './ChordFingeringSelector';
import type { ChordFingering } from '../api';

interface DiatonicChord {
  key: string;
  scale: string;
  degree: number;
  chordRoot: string;
  chordType: string;
  romanNumeral: string;
  chordNotes: string[];
}

export interface SelectedDiatonicChord {
  chord: DiatonicChord;
  fingering: ChordFingering | null;
}

interface DiatonicChordsProps {
  diatonicChords: DiatonicChord[];
  currentKey: string;
  currentScale: string;
  selectedDiatonicChord?: SelectedDiatonicChord | null;
  onDiatonicChordFingeringSelect?: (chord: DiatonicChord, fingering: ChordFingering | null) => void;
}

const DiatonicChords: React.FC<DiatonicChordsProps> = ({
  diatonicChords,
  currentKey,
  currentScale,
  selectedDiatonicChord,
  onDiatonicChordFingeringSelect,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (!diatonicChords || diatonicChords.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <p className="text-sm text-muted-foreground">
            No diatonic chords available. Make sure you're in Scale mode.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Guitar className="h-4 w-4" />
          Diatonic Chords - {currentKey} {currentScale}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-full">
          <div className="p-4 space-y-2">
            {diatonicChords.map((chord, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <Card key={index} className="border border-border">
                  <div
                    className="flex items-center justify-between cursor-pointer w-full px-3 py-2 hover:bg-muted/50 transition"
                    onClick={() => setExpandedIndex(isExpanded ? null : index)}
                    aria-expanded={isExpanded}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className="text-xs font-mono">
                        {chord.romanNumeral}
                      </Badge>
                      <div>
                        <p className="font-medium text-sm">
                          {chord.chordRoot}
                          {chord.chordType}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {chord.chordNotes.join(' - ')}
                        </p>
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>

                  {isExpanded && onDiatonicChordFingeringSelect && (
                    <div className="p-3">
                      <ChordFingeringSelector
                        chordRoot={chord.chordRoot}
                        chordType={chord.chordType}
                        // Fix: pass (null, null) to CLEAR EVERYTHING, pass (chord, fingering) otherwise
                        onFingeringSelect={fingering => {
                          if (fingering == null) {
                            onDiatonicChordFingeringSelect(null, null);
                          } else {
                            onDiatonicChordFingeringSelect(chord, fingering);
                          }
                        }}
                        selectedFingering={
                          selectedDiatonicChord &&
                          selectedDiatonicChord.chord.degree === chord.degree &&
                          selectedDiatonicChord.chord.chordRoot === chord.chordRoot &&
                          selectedDiatonicChord.chord.chordType === chord.chordType
                            ? selectedDiatonicChord.fingering
                            : null
                        }
                      />
                    </div>

                  )}
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default DiatonicChords;
